import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { BadStatusError, cfetch, containsNetworkError } from '@confluence/network';

import { startCommentAgentReactionRefresh } from './commentAgentReactionRefresh.ts';

const REACTION_WAIT_TIMEOUT_MS = 2000;
const MAX_TRACKED_ATTEMPTS = 500;
const attempts = new Map<string, Promise<void>>();

type ReactionRequest = {
	commentId: string;
	agentIdentityAccountId: string;
	emojiId: string;
};

function recordOutcome(
	createAnalyticsEvent: CreateUIAnalyticsEvent | undefined,
	action: 'attempted' | 'failed' | 'timedOut',
	attributes?: { reason: string; statusCode?: number },
): void {
	try {
		createAnalyticsEvent?.({
			type: 'sendOperationalEvent',
			data: {
				action,
				actionSubject: 'commentAgentEyesReaction',
				source: 'agentMention',
				...(attributes && { attributes }),
			},
		}).fire();
	} catch {
		// Reporting is best effort and must never block agent invocation.
	}
}

function classifyFailure(error: unknown): { reason: string; statusCode?: number } {
	if (
		BadStatusError.isBadStatusError(error) &&
		typeof error === 'object' &&
		error !== null &&
		'response' in error &&
		typeof error.response === 'object' &&
		error.response !== null &&
		'status' in error.response &&
		typeof error.response.status === 'number'
	) {
		const statusCode = error.response.status;
		const reasons: Partial<Record<number, string>> = {
			400: 'badRequest',
			401: 'unauthorized',
			403: 'forbidden',
			404: 'notFound',
			429: 'rateLimited',
		};
		return {
			reason: reasons[statusCode] ?? (statusCode >= 500 ? 'serverError' : 'unknown'),
			statusCode,
		};
	}
	return {
		reason: error instanceof Error && containsNetworkError(error) ? 'network' : 'unknown',
	};
}

/** One attempt per saved comment/agent. Resolves by two seconds even if abort never settles. */
export function addCommentAgentEyesReaction(
	payload: ReactionRequest,
	cloudId: string,
	contentId: string,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
): Promise<void> {
	const key = JSON.stringify([cloudId, payload.commentId, payload.agentIdentityAccountId]);
	const existing = attempts.get(key);
	if (existing) {
		return existing;
	}
	if (attempts.size >= MAX_TRACKED_ATTEMPTS) {
		const oldestKey = attempts.keys().next().value;
		if (oldestKey) {
			attempts.delete(oldestKey);
		}
	}

	const wait = new Promise<void>((resolve) => {
		const controller = new AbortController();
		let settled = false;
		const finish = () => {
			if (settled) {
				return;
			}
			settled = true;
			clearTimeout(timeout);
			startCommentAgentReactionRefresh(
				cloudId,
				contentId,
				payload.commentId,
				payload.agentIdentityAccountId,
			);
			resolve();
		};
		const timeout = setTimeout(() => {
			recordOutcome(createAnalyticsEvent, 'timedOut');
			// Release the wait before abort: aborting a browser request cannot guarantee
			// cancellation of a downstream write already underway in CAP.
			finish();
			controller.abort();
		}, REACTION_WAIT_TIMEOUT_MS);
		recordOutcome(createAnalyticsEvent, 'attempted');
		void Promise.resolve()
			.then(() =>
				cfetch('/gateway/api/assist/api/confluence/v1/agentic-reactions', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
						'X-Product': 'confluence',
						'X-Experience-Id': 'ai-mate',
						'X-Cloudid': cloudId,
					},
					body: JSON.stringify(payload),
					signal: controller.signal,
					ignoreNoNetworkError: true,
				}),
			)
			.catch((error: unknown) => {
				if (!settled) {
					recordOutcome(createAnalyticsEvent, 'failed', classifyFailure(error));
				}
			})
			.finally(finish);
	});
	attempts.set(key, wait);
	return wait;
}

export function clearCommentAgentReactionAttemptsForTesting(): void {
	attempts.clear();
}
